import { Group, Select, TextInput } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { Pagination } from '@sportybot-web/components/pagination/pagination';
import { ReelClipCard } from '@sportybot-web/features/clips/components/clip-card/reel-clip-card/reel-clip-card';
import { ClipGrid } from '@sportybot-web/features/clips/components/clip-grid/clip-grid';
import { DEBOUNCED_INPUT_DELAY } from '@sportybot-web/features/clips/constants';
import { reelsSearchSchema } from '@sportybot-web/features/clips/schema/clips-search-schema';
import { userClipsOptions } from '@sportybot-web/services/sporty-gateway/queries';
import { ReelsSortFieldOption } from '@sportybot-web/services/sporty-gateway/types';
import { createReelsSearchActions } from '@sportybot-web/utils/navigation/navigation-helper';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';
import { Search } from 'lucide-react';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_authenticated/clips/my-reels')({
  component: RouteComponent,
  validateSearch: reelsSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        sortBy: ReelsSortFieldOption.CREATED_AT,
        title: '',
      }),
    ],
  },
});
/* eslint-enable perfectionist/sort-objects */

const SORT_OPTIONS = [
  { label: 'Created At', value: ReelsSortFieldOption.CREATED_AT },
  { label: 'Title', value: ReelsSortFieldOption.TITLE },
];

function RouteComponent() {
  const { limit, page, sortBy, title } = useSearch({ from: Route.id });
  const navigate = useNavigate({ from: Route.fullPath });

  const { data, error, isFetching, isLoading, refetch, status } = useQuery({
    ...userClipsOptions({ limit, page, sortBy, title }),
    select: (data) => data.userClips,
  });

  const total = data?.total ?? 0;

  const { changeLimit, changePage, changeSortBy, changeTitle } = createReelsSearchActions(
    (updater) => navigate({ replace: true, search: updater }),
  );

  const handleChangeTitle = useDebouncedCallback((value: string | undefined) => {
    void changeTitle(value);
  }, DEBOUNCED_INPUT_DELAY);

  return (
    <ClipGrid
      data={data?.userClips ?? []}
      error={error}
      filters={
        <Group justify="space-between" w="100%">
          <Group>
            <TextInput
              data-testid="title-input"
              defaultValue={title ?? ''}
              leftSection={<Search height={14} />}
              onChange={(event) => handleChangeTitle(event.currentTarget.value)}
              placeholder="Title"
            />
            <Select
              aria-label="Sort Order"
              data={SORT_OPTIONS}
              data-testid="sort-dropdown"
              onChange={(value) => changeSortBy(value ?? ReelsSortFieldOption.CREATED_AT)}
              size="sm"
              value={sortBy}
              w={130}
            />
          </Group>
          <Pagination
            initialLoading={isLoading}
            limit={limit}
            loading={isFetching && !isLoading}
            onChangeLimit={changeLimit}
            onChangePage={changePage}
            page={page}
            selectLabel="Clips Per Page"
            selectOptions={[16, 32, 48]}
            total={total}
          />
        </Group>
      }
      refetch={refetch}
      renderCard={(clip) => (clip ? <ReelClipCard clip={clip} key={clip.id} /> : undefined)}
      status={status}
    />
  );
}
